import EditIcon from "@mui/icons-material/Edit";
import { CardHeader, CircularProgress, Divider, IconButton } from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { unwrapResult } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { isAbortError, isEmpty, isPartReturnRequested } from "helpers";
import { UsedPart } from "models/usedPart";
import { StockStore } from "operations/schema/schema";
import { useAppDispatch, useAppSelector } from "store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import {
  selectSelectedJob,
  selectSelectedJobVisit,
  setTravelMileage,
  updateVisitInformation,
} from "store/slices/jobs.store";
import { selectEngineerSettings } from "store/slices/user.store";

import BackdropWhite from "components/BackdropWhite";
import { PromptDialog } from "components/PromptDialog";
import StyledTextField from "components/StyledTextField";
import { TotalTimeComponent } from "../times/TotalTimeComponent";
import { asyncQueries } from "store/slices/jobs.queries";

interface JobVisitSummaryProps {
  isEngineerSummaryPage?: boolean;
}

export const JobVisitSummary = (props: JobVisitSummaryProps) => {
  const dispatch = useAppDispatch();
  const job = useAppSelector(selectSelectedJob);
  const { usedParts, workTimes, solutionDescription, travelTimes, travelMileage } =
    useAppSelector(selectSelectedJobVisit);
  const intl = useIntl();
  const engineerSettings = useAppSelector(selectEngineerSettings);
  const showPriceOfJob = engineerSettings?.showPriceOfJob;

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [miles, setMiles] = useState(travelMileage || 0);
  const [tempMiles, setTempMiles] = useState(miles);
  const [loading, setLoading] = useState(false);

  const travelEnd = travelTimes[travelTimes.length - 1]?.stopTime
    ? new Date(travelTimes[travelTimes.length - 1].stopTime!)
    : null;

  const handleApiSuccess = () => {
    setMiles(tempMiles);
    setLoading(false);
  };

  useEffect(() => {
    const fetchJobPricing = async () => {
      try {
        const items = usedParts.map((jobPart: UsedPart) => ({
          itemId: Number(jobPart.part.id),
          quantity: Number(jobPart.part.quantity),
        }));

      let workUnitStartDate = new Date().toISOString();
      let workUnitEndDate = new Date().toISOString();

      const workTime = workTimes[0];
      if (workTime) {
        if (workTime.startDate && workTime.startTime) {
          const startDatePart = new Date(workTime.startDate).toISOString().split("T")[0];
          const startTimePart = new Date(workTime.startTime).toISOString().split("T")[1];
          workUnitStartDate = new Date(`${startDatePart}T${startTimePart}`).toISOString();
        }

        if (workTime.stopDate && workTime.stopTime) {
          const stopDatePart = new Date(workTime.stopDate).toISOString().split("T")[0];
          const stopTimePart = new Date(workTime.stopTime).toISOString().split("T")[1];
          workUnitEndDate = new Date(`${stopDatePart}T${stopTimePart}`).toISOString();
        }
      }

       await dispatch(
          asyncQueries.getJobPricing({
            jobId: job.id,
            workUnitStartDate: workUnitStartDate,
            workUnitEndDate: workUnitEndDate,
            items: items
          })
        );
      } catch (err) {
        console.error("Error fetching job pricing:", err);
      }
    };
    
    if (showPriceOfJob) {
      fetchJobPricing();
    }
  }, [dispatch, job.id, showPriceOfJob, usedParts, workTimes]);

  const jobUsedParts = usedParts
    .filter(
      (jobPart: UsedPart) =>
        jobPart.part.stockStore !== StockStore.Other && !isPartReturnRequested(jobPart.part)
    )
    .map((jobPart: UsedPart) => {
      return {
        id: jobPart.part.id,
        quantity: jobPart.part.quantity,
        partNumber: jobPart.part.partNumber,
        stockId: jobPart.part.stockId,
        stockStore: jobPart.part.stockStore,
        description: jobPart.part.description,
        storeLabel: jobPart.part.stockStore === StockStore.Nonstock ? jobPart.part.storeLabel : "",
      };
    });

  const jobRequestedParts = usedParts
    .filter((jobPart: UsedPart) => jobPart.part.stockStore === StockStore.Other)
    .map((jobPart: UsedPart) => {
      return {
        id: jobPart.part.id,
        quantity: jobPart.part.quantity,
        partNumber: jobPart.part.partNumber,
        stockId: jobPart.part.stockId,
        stockStore: jobPart.part.stockStore,
        description: jobPart.part.description,
      };
    });

  const jobReturnedParts = usedParts
    .filter(
      (jobPart: UsedPart) =>
        jobPart.part.stockStore !== StockStore.Other && isPartReturnRequested(jobPart.part)
    )
    .map((jobPart: UsedPart) => {
      return {
        id: jobPart.part.id,
        quantity: jobPart.part.quantity,
        partNumber: jobPart.part.partNumber,
        stockId: jobPart.part.stockId,
        stockStore: jobPart.part.stockStore,
        description: jobPart.part.description,
        storeLabel: jobPart.part.stockStore === StockStore.Nonstock ? jobPart.part.storeLabel : "",
      };
    });

  const handleEditDialogClose = () => {
    setTempMiles(miles);
  };

  const handleMilesChange = (e: any) => {
    const { value } = e.target;
    //validate if its a number
    if (/^\d+$/.test(value) || value === "") {
      setTempMiles(Number(value));
    }
  };

  const hostname = window.location.host;

  let currency = "GBP";

  if (hostname.toLowerCase().includes("winserv")) {
    currency = "SEK";     
  } else if (hostname.toLowerCase().includes("tivapp")) {
    currency = "EUR";     
  } 

  const formatCurrency = (value: number, currency: string) =>
    new Intl.NumberFormat(intl.locale, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
    }).format(value);

  return (
    <>
      <Card elevation={1}>
        <CardHeader
          action={
            !isEmpty(travelTimes) &&
            props?.isEngineerSummaryPage && (
              <IconButton
                aria-label={intl.formatMessage({ id: "general.edit" })}
                onClick={() => setOpenEditDialog(true)}
                size="large"
              >
                <EditIcon color="action" />
              </IconButton>
            )
          }
          title={intl.formatMessage({ id: "visit.visitInformation" })}
          sx={{ paddingBottom: "0px" }}
        />
        <CardContent sx={{ paddingTop: "0px" }}>
          <List dense>
            {travelEnd !== null && (
              <ListItem key="Travel time">
                <ListItemText
                  key="Travel time text"
                  primaryTypographyProps={{ variant: "body1" }}
                  secondaryTypographyProps={{
                    variant: "subtitle2",
                    color: "textPrimary",
                  }}
                  primary={`${intl.formatMessage({ id: "times.travelTime" })}:`}
                  secondary={<TotalTimeComponent timesMerged={travelTimes} />}
                />
              </ListItem>
            )}
            {!isEmpty(travelTimes) && props?.isEngineerSummaryPage && (
              <>
                <ListItem key="Total Miles">
                  <ListItemText
                    key="Total miles text"
                    primaryTypographyProps={{ variant: "body1" }}
                    secondaryTypographyProps={{
                      variant: "subtitle2",
                      color: "textPrimary",
                    }}
                    primary={`${intl.formatMessage({ id: "visit.totalMiles" })}:`}
                    secondary={miles}
                  />
                </ListItem>
              </>
            )}
            <ListItem key="Work time">
              <ListItemText
                key="Work time text"
                primaryTypographyProps={{ variant: "body1" }}
                secondaryTypographyProps={{
                  variant: "subtitle2",
                  color: "textPrimary",
                }}
                primary={`${intl.formatMessage({ id: "times.workTime" })}:`}
                secondary={<TotalTimeComponent timesMerged={workTimes} />}
              />
            </ListItem>
            {showPriceOfJob && (
              <>
                {job.jobPricing ? (
                  <>
                    <ListItem key="Callout Price" sx={{ py: 0 }}>
                      <ListItemText
                        primary={
                          <span>
                            <span style={{ display: "inline-block", width: "140px" }}>
                              {intl.formatMessage({ id: "job.callout" })}:
                            </span>
                            {formatCurrency(job?.jobPricing?.calloutPrice ?? 0, currency)}
                          </span>
                        }
                        primaryTypographyProps={{ variant: "body1" }}
                      />
                    </ListItem>
                    <ListItem key="Labour Price" sx={{ py: 0 }}>
                      <ListItemText
                        primary={
                          <span>
                            <span style={{ display: "inline-block", width: "140px" }}>
                              {intl.formatMessage({ id: "job.labour" })}:
                            </span>
                            {formatCurrency(job?.jobPricing?.labourPrice ?? 0, currency)}
                          </span>
                        }
                        primaryTypographyProps={{ variant: "body1" }}
                      />
                    </ListItem>
                    <ListItem key="Parts Price" sx={{ py: 0 }}>
                      <ListItemText
                        primary={
                          <span>
                            <span style={{ display: "inline-block", width: "140px" }}>
                              {intl.formatMessage({ id: "job.parts" })}:
                            </span>
                            {formatCurrency(job?.jobPricing?.partsPrice ?? 0, currency)}
                          </span>
                        }
                        primaryTypographyProps={{ variant: "body1" }}
                      />
                    </ListItem>
                    <ListItem key="Travel Price" sx={{ py: 0 }}>
                      <ListItemText
                        primary={
                          <span>
                            <span style={{ display: "inline-block", width: "140px" }}>
                              {intl.formatMessage({ id: "job.travel" })}:
                            </span>
                            {formatCurrency(job?.jobPricing?.travelPrice ?? 0, currency)}
                          </span>
                        }
                        primaryTypographyProps={{ variant: "body1" }}
                      />
                    </ListItem>
                    <ListItem key="Fixed Charge Price" sx={{ py: 0 }}>
                      <ListItemText
                        primary={
                          <span>
                            <span style={{ display: "inline-block", width: "140px" }}>
                              {intl.formatMessage({ id: "job.fixedCharge" })}:
                            </span>
                            {formatCurrency(job?.jobPricing?.fixedChargePrice ?? 0, currency)}
                          </span>
                        }
                        primaryTypographyProps={{ variant: "body1" }}
                      />
                    </ListItem>
                    <ListItem key="Mileage Price" sx={{ py: 0 }}>
                      <ListItemText
                        primary={
                          <span>
                            <span style={{ display: "inline-block", width: "140px" }}>
                              {intl.formatMessage({ id: "job.mileage" })}:
                            </span>
                            {formatCurrency(job?.jobPricing?.mileagePrice ?? 0, currency)}
                          </span>
                        }
                        primaryTypographyProps={{ variant: "body1" }}
                      />
                    </ListItem>
                    <Divider sx={{ borderBottomWidth: 2, my: 0.5 }} />
                    <ListItem key="Net Price" sx={{ py: 0 }}>
                      <ListItemText
                        primary={
                          <span style={{ fontWeight: "700" }}>
                            <span style={{ display: "inline-block", width: "140px" }}>
                              {intl.formatMessage({ id: "job.netPrice" })}:
                            </span>
                            {formatCurrency(job?.jobPricing?.netPrice ?? 0, currency)}
                          </span>
                        }
                        primaryTypographyProps={{ variant: "body1" }}
                      />
                    </ListItem>
                    <ListItem key="VAT Price" sx={{ py: 0 }}>
                      <ListItemText
                        primary={
                          <span>
                            <span style={{ display: "inline-block", width: "140px" }}>
                              {`${intl.formatMessage({ id: "job.vatPrice" })} (${((job?.jobPricing?.vatRate ?? 0) * 100).toFixed(0)}%)`}:
                            </span>
                            {formatCurrency(job?.jobPricing?.vatPrice ?? 0, currency)}
                          </span>
                        }
                        primaryTypographyProps={{ variant: "body1" }}
                      />
                    </ListItem>
                    <ListItem key="Total Price" sx={{ py: 0 }}>
                      <ListItemText
                        primary={
                          <span style={{ fontWeight: "700" }}>
                            <span style={{ display: "inline-block", width: "140px" }}>
                              {intl.formatMessage({ id: "job.totalPrice" })}:
                            </span>
                            {formatCurrency(job?.jobPricing?.totalPrice ?? 0, currency)}
                          </span>
                        }
                        primaryTypographyProps={{ variant: "body1" }}
                      />
                    </ListItem>
                  </>
                ) : (
                  <>
                    {["job.callout", "job.labour", "job.parts", "job.travel", "job.fixedCharge", "job.mileage", "job.netPrice", "job.vatPrice", "job.totalPrice"].map((id) => (
                      <ListItem key={id} sx={{ py: 0 }}>
                        <ListItemText
                          primary={
                            <span>
                              <span style={{ display: "inline-block", width: "140px" }}>
                                {intl.formatMessage({ id })}:
                              </span>
                              <Skeleton
                                variant="text"
                                width={120}
                                height={22}
                                sx={{ display: "inline-block", verticalAlign: "middle" }}
                              />
                            </span>
                          }
                          primaryTypographyProps={{ variant: "body1" }}
                        />
                      </ListItem>
                    ))}
                  </>
                )}
              </>
            )}
            {solutionDescription !== "" && (
              <ListItem key="Solution">
                <ListItemText
                  key="Solution text"
                  primaryTypographyProps={{ variant: "body1" }}
                  secondaryTypographyProps={{
                    variant: "subtitle2",
                    color: "textPrimary",
                  }}
                  primary={`${intl.formatMessage({
                    id: "job.solutionDescription",
                  })}:`}
                  secondary={solutionDescription}
                />
              </ListItem>
            )}
            {jobUsedParts.length > 0 && (
              <ListItem key="visit.usedParts">
                <ListItemText
                  key="Used parts text"
                  primaryTypographyProps={{ variant: "body1" }}
                  secondaryTypographyProps={{
                    variant: "subtitle2",
                    color: "textPrimary",
                  }}
                  primary={`${intl.formatMessage({ id: "visit.usedParts" })}:`}
                  secondary={jobUsedParts.map((usedPart, index) => {
                    return (
                      <div key={index}>
                        <div className="truncated">
                          {`${usedPart.quantity} x ${usedPart.description}${
                            usedPart.storeLabel ? ` (${usedPart.storeLabel})` : ""
                          }`}
                        </div>
                      </div>
                    );
                  })}
                />
              </ListItem>
            )}
            {jobRequestedParts.length > 0 && (
              <ListItem key="Requested parts">
                <ListItemText
                  key="Requested parts text"
                  primaryTypographyProps={{ variant: "body1" }}
                  secondaryTypographyProps={{
                    variant: "subtitle2",
                    color: "textPrimary",
                  }}
                  primary={`${intl.formatMessage({ id: "part.requestedParts" })}:`}
                  secondary={jobRequestedParts.map((requestedPart, index) => (
                    <div key={index}>
                      <div className="truncated">
                        {`${requestedPart.quantity} x ${requestedPart.description}`}
                      </div>
                    </div>
                  ))}
                />
              </ListItem>
            )}
            {jobReturnedParts.length > 0 && (
              <ListItem key="Returned parts">
                <ListItemText
                  key="Returned parts text"
                  primaryTypographyProps={{ variant: "body1" }}
                  secondaryTypographyProps={{
                    variant: "subtitle2",
                    color: "textPrimary",
                  }}
                  primary={`${intl.formatMessage({ id: "part.returnedParts" })}:`}
                  secondary={jobReturnedParts.map((returnedParts, index) => (
                    <div className="truncated" key={index}>
                      {`${returnedParts.quantity} x ${returnedParts.description}`}
                    </div>
                  ))}
                />
              </ListItem>
            )}
          </List>
        </CardContent>
      </Card>
      <PromptDialog
        aria-labelledby="form-dialog-title"
        open={openEditDialog}
        setOpen={setOpenEditDialog}
        okText={intl.formatMessage({ id: "general.save" })}
        onOk={() => {
          setLoading(true);
          dispatch(setTravelMileage({ mileage: tempMiles }));
          dispatch(updateVisitInformation({ jobId: job.id }))
            .then(unwrapResult)
            .then(handleApiSuccess)
            .catch((e) => {
              if (isAbortError(e)) return;
              dispatch(addSnackbarMessage({ key: "UpdateVisitInformation-fail" }));
              setLoading(false);
            });
        }}
        onCancel={handleEditDialogClose}
        title={<FormattedMessage id="visit.editVisitInformation" />}
        promptContent={
          <>
            <BackdropWhite open={loading} onClick={handleEditDialogClose}>
              <CircularProgress color="inherit" />
            </BackdropWhite>
            <StyledTextField
              id="total-miles"
              name="totalMiles"
              label={intl.formatMessage({ id: "visit.totalMiles" })}
              value={tempMiles}
              onChange={handleMilesChange}
              sx={{ my: 1 }}
            />
          </>
        }
      />
    </>
  );
};
